<script setup lang="ts">
/**
 * Multi-series year lines — the "same money, four lenses" chart.
 *
 * <YearBars> already plots one yearly figure; this plots SEVERAL against the
 * same axis, which is the whole point of the spending-evolution page: nominal
 * and real pesos side by side is what shows how much of a rise is inflation.
 * Kept separate from YearBars rather than bolted onto it — bars answer "how big
 * was each year", lines answer "how did the years move relative to each other",
 * and merging them would mean a props object that is really two components.
 *
 * A `dashed` series reads as "not the headline number" (the as-reported series,
 * which still contains the artefacts). Client-only: canvas cannot SSR, and the
 * placeholder keeps the same height so hydration does not shift the page.
 */
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import { Line } from 'vue-chartjs'

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)

export interface TrendSeries {
  label: string
  values: (number | null)[]
  /** Token name without the leading dashes, e.g. `celeste-deep`. */
  colorVar: string
  fallback: string
  /** Renders dashed — used for the unsanitised "as reported" series. */
  dashed?: boolean
}

const props = withDefaults(defineProps<{
  labels: string[]
  series: TrendSeries[]
  /** How to format the axis and tooltip values. `plain` is a bare number with
   *  `decimals` places — for rates and indices, which are neither money nor a
   *  share and must not be compacted ("10,3 cada 100.000", not "10"). */
  format?: 'money' | 'usd' | 'pct' | 'count' | 'plain'
  /** Decimal places for `format: 'plain'`. */
  decimals?: number
  /** Appended to the tooltip value, e.g. "cada 100.000 hab.". */
  unit?: string
  height?: number
  /** Index of a year to mark as incomplete (drawn hollow). */
  partialIndex?: number | null
  /** What the chart shows. Becomes its accessible name and the table caption. */
  label: string
}>(), { format: 'money', height: 300, partialIndex: null, decimals: 1, unit: '' })

const { t, locale } = useI18n()

/**
 * Bare value, no unit. The unit belongs to the axis as a whole, not to every
 * tick: repeating it turned each y label into "0,0 cada 100.000 hab." and the
 * axis ate the plot. It is appended once, in the tooltip and the table caption.
 */
function fmt(v?: number | null): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  if (props.format === 'plain') {
    return new Intl.NumberFormat(locale.value === 'en' ? 'en-US' : 'es-UY', {
      minimumFractionDigits: props.decimals,
      maximumFractionDigits: props.decimals,
    }).format(v)
  }
  if (props.format === 'pct') return `${(v * 100).toFixed(2).replace('.', ',')}%`
  if (props.format === 'count') return formatCount(v)
  if (props.format === 'usd') return formatMoney(v, 'USD', { compact: true })
  return formatMoney(v, 'UYU', { compact: true })
}

/** Says the unit once, where there is room for it. */
function withUnit(s: string): string {
  return props.unit ? `${s} ${props.unit}` : s
}

// Read live token values so the chart follows the theme toggle instead of
// freezing whatever was set at mount (same approach as YearBars/InvHBars).
const theme = ref<Record<string, string>>({ text: '#64757f', rule: '#d3dade', ink: '#0f2233', surface: '#ffffff' })
function readTokens() {
  if (!import.meta.client) return
  const cs = getComputedStyle(document.documentElement)
  const v = (n: string, f: string) => cs.getPropertyValue(n).trim() || f
  const next: Record<string, string> = {
    text: v('--text-muted', '#64757f'),
    rule: v('--rule', '#d3dade'),
    ink: v('--ink', '#0f2233'),
    surface: v('--surface', '#ffffff'),
  }
  for (const s of props.series) next[s.colorVar] = v(`--${s.colorVar}`, s.fallback)
  theme.value = next
}

onMounted(() => {
  readTokens()
  const mo = new MutationObserver(readTokens)
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] })
  onBeforeUnmount(() => mo.disconnect())
})

// Chart.js defers a resize while its entry animation runs, so a viewport change
// in those first frames leaves the canvas at its old width. Re-measure explicitly.
const wrap = ref<HTMLElement | null>(null)
const lineRef = ref<any>(null)
let ro: ResizeObserver | undefined
onMounted(() => {
  if (!wrap.value || typeof ResizeObserver === 'undefined') return
  ro = new ResizeObserver(() => lineRef.value?.chart?.resize())
  ro.observe(wrap.value)
})
onBeforeUnmount(() => ro?.disconnect())

const chartData = computed(() => ({
  labels: props.labels,
  datasets: props.series.map(s => ({
    label: s.label,
    data: s.values,
    borderColor: theme.value[s.colorVar] ?? s.fallback,
    backgroundColor: theme.value[s.colorVar] ?? s.fallback,
    borderWidth: s.dashed ? 1.5 : 2.5,
    borderDash: s.dashed ? [5, 4] : [],
    tension: 0.25,
    pointRadius: (ctx: any) => (ctx.dataIndex === props.partialIndex ? 4 : 2.5),
    pointStyle: (ctx: any) => (ctx.dataIndex === props.partialIndex ? 'crossRot' : 'circle'),
    pointHoverRadius: 5,
    // A missing year stays missing. `spanGaps: true` would draw straight
    // through a year with no observation — on a page whose whole claim is that
    // it does not invent data, an interpolated segment is a lie in ink.
    spanGaps: false,
  })),
}))

/**
 * Chart.js animates in JavaScript, so the global
 * `@media (prefers-reduced-motion: reduce)` rule in main.scss cannot reach it —
 * the 420ms sweep replayed on every lens switch regardless of the setting.
 * Read the query directly and turn the animation off instead of shortening it:
 * the chart still redraws, it just arrives without the motion.
 */
const reduceMotion = ref(false)
onMounted(() => {
  const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)')
  if (!mq) return
  reduceMotion.value = mq.matches
  const onChange = (e: MediaQueryListEvent) => {
    reduceMotion.value = e.matches
  }
  mq.addEventListener('change', onChange)
  onBeforeUnmount(() => mq.removeEventListener('change', onChange))
})

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  animation: reduceMotion.value ? (false as const) : { duration: 420, easing: 'easeOutQuart' as const },
  plugins: {
    legend: {
      display: props.series.length > 1,
      position: 'top' as const,
      align: 'start' as const,
      labels: {
        boxWidth: 10,
        boxHeight: 10,
        usePointStyle: true,
        pointStyle: 'line',
        color: theme.value.text,
        font: { family: 'IBM Plex Mono, monospace', size: 11 },
      },
    },
    tooltip: {
      backgroundColor: theme.value.ink,
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: 10,
      cornerRadius: 6,
      titleFont: { family: 'IBM Plex Mono, monospace', size: 12, weight: 700 as const },
      bodyFont: { family: 'IBM Plex Mono, monospace', size: 12 },
      callbacks: {
        title: (items: any[]) => {
          const label = items[0]?.label ?? ''
          return items[0]?.dataIndex === props.partialIndex ? `${label} · ${t('evolucion.partialShort')}` : label
        },
        label: (ctx: any) => `${ctx.dataset.label}: ${withUnit(fmt(ctx.parsed.y))}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { color: theme.value.rule },
      ticks: {
        color: theme.value.text,
        font: { family: 'IBM Plex Mono, monospace', size: 11 },
        autoSkip: true,
        maxRotation: 0,
        minRotation: 0,
        autoSkipPadding: 12,
      },
    },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: theme.value.rule, drawTicks: false },
      ticks: {
        color: theme.value.text,
        font: { family: 'IBM Plex Mono, monospace', size: 11 },
        maxTicksLimit: 6,
        padding: 6,
        callback: (v: number | string) => fmt(Number(v)),
      },
    },
  },
}))
</script>

<template>
  <div class="tl-wrap">
    <div
      ref="wrap"
      class="tl"
      :style="{ height: `${height}px` }"
    >
      <ClientOnly>
        <Line
          ref="lineRef"
          :data="chartData"
          :options="chartOptions"
          :aria-label="label"
        />
        <template #fallback>
          <div
            class="tl__ph"
            aria-hidden="true"
          />
        </template>
      </ClientOnly>
    </div>

    <!--
      A canvas has no readable content: Chart.js stamps `role="img"` on it, so a
      screen reader announces "image" and stops. On a transparency site the
      series IS the evidence, and on this page it exists nowhere else in text —
      so the same numbers ship as a real table, visually hidden. It is rendered
      server-side, which also makes the figures crawlable.
    -->
    <!--
      The utility goes on a WRAPPER, never on the <table> itself. Table layout
      treats `width`/`height` as minimums and grows to its content regardless,
      so a `.u-visually-hidden` table is still ~450x1300px of real layout. That
      is invisible until the chart sits inside <ChartBlock>'s scroller, where it
      became a phantom vertical scrollbar and a mostly empty chart frame. A div
      honours the 1px box and clips the table inside it.
    -->
    <div class="u-visually-hidden">
      <table>
        <caption>{{ withUnit(label) }}</caption>
        <thead>
          <tr>
            <th scope="col">
              {{ t('evolucion.a11y.period') }}
            </th>
            <th
              v-for="s in series"
              :key="s.label"
              scope="col"
            >
              {{ s.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(l, i) in labels"
            :key="l"
          >
            <th scope="row">
              {{ i === partialIndex ? `${l} (${t('evolucion.partialShort')})` : l }}
            </th>
            <td
              v-for="s in series"
              :key="s.label"
            >
              {{ fmt(s.values[i]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.tl-wrap { position: relative; min-width: 0; }
.tl { position: relative; width: 100%; min-width: 0; }
.tl__ph {
  width: 100%;
  height: 100%;
  border-radius: var(--r-md);
  background: var(--surface-sunken);
}
</style>
